import { useEffect, useRef, useState } from 'react';

interface UseIntervalProps<JobFn extends (...args: any[]) => any> {
  interval: number;
  job: JobFn;
}

// 条件类型提取 job 执行结果类型
type JobResult<T> = T extends (...args: any[]) => Promise<infer R>
  ? R
  : T extends (...args: any[]) => infer R
    ? R
    : never;

type IntervalReturn<JobFn extends (...args: any[]) => any> = {
  running: boolean;
  result: JobResult<JobFn> | undefined;
};

export function useInterval<JobFn extends (...args: any[]) => any>({
  interval,
  job,
}: UseIntervalProps<JobFn>): IntervalReturn<JobFn> {
  const [result, setResult] = useState<JobResult<JobFn> | undefined>(undefined);
  const [isRunning, setIsRunning] = useState(false);
  const savedJob = useRef(job);

  useEffect(() => {
    savedJob.current = job;
  }, [job]);

  useEffect(() => {
    let canceled = false;

    const runJob = async () => {
      setIsRunning(true);
      const res = await savedJob.current();
      if (!canceled) setResult(res);
      setIsRunning(false);
    };

    // 启动时立即执行一次
    runJob();

    const id = setInterval(runJob, interval);
    return () => {
      canceled = true;
      clearInterval(id);
    };
  }, [interval]);

  return { result, running: isRunning };
}
